import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiHeart, FiMapPin, FiCamera, FiCalendar } from 'react-icons/fi';

const AboutContainer = styled.div`
  min-height: 100vh;
  padding: var(--space-2xl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--space-3xl);
  
  h1 {
    font-size: 3rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-lg);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  .subtitle {
    font-size: 1.25rem;
    color: var(--neutral-gray-600);
    max-width: 600px;
    margin: 0 auto var(--space-2xl);
  }
`;

const FamilyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-3xl);
  margin-bottom: var(--space-3xl);
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const FamilyImage = styled(motion.div)`
  background: url('/images/about.jpg');
  background-size: cover;
  background-position: center;
  height: 500px;
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

const FamilyContent = styled(motion.div)`
  h2 {
    font-size: 2rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-lg);
  }
  
  p {
    font-size: 1.125rem;
    line-height: 1.7;
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-lg);
  }
`;

const StatsSection = styled.section`
  background: var(--neutral-gray-50);
  border-radius: var(--radius-2xl);
  padding: var(--space-3xl);
  margin: var(--space-3xl) 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-2xl);
  text-align: center;
`;

const StatCard = styled(motion.div)`
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--primary-green), var(--primary-green-light));
    border-radius: 50%;
    margin-bottom: var(--space-lg);
    color: white;
  }
  
  .number {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  .label {
    color: var(--neutral-gray-600);
    font-weight: 500;
    font-size: 1.125rem;
  }
`;

const TimelineSection = styled.section`
  margin: var(--space-3xl) 0;
  
  h2 {
    text-align: center;
    font-size: 2.5rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-3xl);
  }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, var(--primary-green), var(--secondary-blue));
    border-radius: 2px;
    
    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: var(--space-3xl);
  
  &:nth-child(odd) {
    padding-left: 60%;
    
    @media (max-width: 768px) {
      padding-left: 60px;
      padding-right: 0;
    }
  }
  
  &:nth-child(even) {
    padding-right: 60%;
    text-align: right;
    
    @media (max-width: 768px) {
      padding-left: 60px;
      padding-right: 0;
      text-align: left;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    width: 16px;
    height: 16px;
    background: var(--primary-green);
    border: 4px solid white;
    border-radius: 50%;
    box-shadow: var(--shadow-md);
    
    ${props => props.index % 2 === 0 ? `
      left: 50%;
      transform: translateX(-50%);
    ` : `
      left: 50%;
      transform: translateX(-50%);
    `}
    
    @media (max-width: 768px) {
      left: 12px;
      transform: none;
    }
  }
`;

const TimelineContent = styled.div`
  background: white;
  padding: var(--space-xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  
  .year {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-green);
    margin-bottom: var(--space-sm);
  }
  
  h3 {
    font-size: 1.25rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-md);
  }
  
  p {
    color: var(--neutral-gray-600);
    line-height: 1.6;
  }
`;

const About = () => {
  const stats = [
    { icon: <FiMapPin size={24} />, number: "50+", label: "States Visited" },
    { icon: <FiCalendar size={24} />, number: "7", label: "Years RVing" },
    { icon: <FiCamera size={24} />, number: "2000+", label: "Photos Taken" },
    { icon: <FiHeart size={24} />, number: "2", label: "Years Full-Time" }
  ];

  const timeline = [
    {
      year: "2018",
      title: "Our RV Journey Begins",
      description: "Luke and Patty purchased their first RV and started taking weekend trips to explore local state parks and campgrounds."
    },
    {
      year: "2019",
      title: "Dobby Joins the Adventure",
      description: "We adopted Dobby, our beloved rescue dog, who quickly became the perfect RV travel companion and our family photographer's favorite subject."
    },
    {
      year: "2020",
      title: "Extended Travel",
      description: "The pandemic gave us the opportunity to work remotely and take our first extended RV trip, visiting national parks across the western United States."
    },
    {
      year: "2021",
      title: "Social Media Growth",
      description: "Started sharing our adventures on Instagram and Facebook, connecting with other RV enthusiasts and building our community."
    },
    {
      year: "2022",
      title: "YouTube Channel Launch",
      description: "Launched our YouTube channel to share longer-form content, RV tips, and detailed park reviews with our growing audience."
    },
    {
      year: "2023",
      title: "Full-Time RV Life",
      description: "Made the big decision to sell our house and become full-time RVers, living our dream of exploring America one park at a time."
    },
    {
      year: "2024",
      title: "Community Building",
      description: "Focused on building a supportive community of RV enthusiasts, hosting meetups and sharing resources to help others on their journey."
    },
    {
      year: "2025",
      title: "RV Better Together Website",
      description: "Launched our comprehensive website to better serve our community with detailed guides, exclusive content, and a central hub for all our adventures."
    }
  ];

  return (
    <AboutContainer>
      <Container>
        <HeroSection>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Meet Luke, Patty & Dobby
          </motion.h1>
          <motion.p
            className="subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Three adventurers sharing the beauty of America's great outdoors, 
            one park at a time. Living proof that life truly is better together on the road.
          </motion.p>
        </HeroSection>

        <FamilyGrid>
          <FamilyImage
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />
          
          <FamilyContent
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Our Story</h2>
            <p>
              What started as weekend camping trips in 2018 has evolved into a full-time 
              adventure across America's most beautiful destinations. Luke and Patty, 
              along with their faithful companion Dobby, have discovered that the best 
              memories are made when you're exploring the great outdoors together.
            </p>
            <p>
              From the towering peaks of the Rocky Mountains to the coastal beauty of 
              the Pacific Northwest, we've learned that every destination has its own 
              unique magic. Our mission is simple: share these incredible experiences 
              and inspire others to discover the joy of RV travel.
            </p>
            <p>
              Whether you're a seasoned RVer or just dreaming of life on the road, 
              we're here to share practical tips, honest reviews, and the kind of 
              authentic stories that only come from truly living the adventure.
            </p>
          </FamilyContent>
        </FamilyGrid>

        <StatsSection>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{stat.icon}</div>
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </StatCard>
            ))}
          </StatsGrid>
        </StatsSection>

        <TimelineSection>
          <h2>Our Journey Timeline</h2>
          <Timeline>
            {timeline.map((item, index) => (
              <TimelineItem
                key={index}
                index={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <TimelineContent>
                  <div className="year">{item.year}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </TimelineSection>
      </Container>
    </AboutContainer>
  );
};

export default About;
